"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useLayoutEffect, useRef, useSyncExternalStore } from "react";
import { usePathname } from "next/navigation";
import dynamic from "next/dynamic";
import { MotionConfig } from "framer-motion";
import ThemeProvider from "@/components/ThemeProvider";
import ScrollToTop from "@/components/ui/ScrollToTop";
import Footer from "@/components/layouts/Footer";
import HomeTextAnimations from "@/components/home/HomeTextAnimations";
import Preloader from "@/components/Preloader";
import PageTransition from "@/components/transitions/PageTransition";
import { readDesktopWidth, readServerAnimationWidth, subscribeToDesktopWidth } from "@/lib/breakpoints";
const Header = dynamic(() => import("@/components/layouts/Header"), {
  ssr: false
});
const ToastHost = dynamic(() => import("@/components/ui/ToastHost"), {
  ssr: false
});
interface LayoutClientProps {
  children: React.ReactNode;
}
function RouteScrollReset() {
  const $ = _c(5);
  const pathname = usePathname();
  const isInitialMount = useRef(true);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      isInitialMount.current = false;
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const hash = window.location.hash;
      if (hash) {
        const id = hash.slice(1);
        let attempts = 0;
        let raf = null;
        const scrollToHash = () => {
          const el = document.getElementById(id);
          if (el) {
            el.scrollIntoView({
              behavior: "instant" as ScrollBehavior,
              block: "start"
            });
            return;
          }
          attempts = attempts + 1;
          attempts;
          if (attempts < 30) {
            raf = requestAnimationFrame(scrollToHash);
          } else {
            window.scrollTo({
              top: 0,
              left: 0,
              behavior: "instant"
            });
          }
        };
        raf = requestAnimationFrame(scrollToHash);
        return () => {
          if (raf !== null) {
            cancelAnimationFrame(raf);
          }
        };
      }
      if (isInitialMount.current) {
        return;
      }
      const scrollTop = _temp;
      scrollTop();
      const frame = requestAnimationFrame(scrollTop);
      const timeout = window.setTimeout(scrollTop, 150);
      return () => {
        cancelAnimationFrame(frame);
        window.clearTimeout(timeout);
      };
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== pathname) {
    t3 = [pathname];
    $[3] = pathname;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  useLayoutEffect(t2, t3);
  return null;
}
function _temp() {
  if (window.location.hash) {
    return;
  }
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: "instant"
  });
}
export default function LayoutClient(t0) {
  const $ = _c(21);
  const {
    children
  } = t0;
  const pathname = usePathname();
  const isBrochurePage = pathname === "/brochure";
  const contentRef = useRef(null);
  const isDesktop = useSyncExternalStore(subscribeToDesktopWidth, readDesktopWidth, readServerAnimationWidth);
  const t1 = isDesktop ? "user" : "always";
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-1000 focus:rounded-md focus:bg-white focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-gray-900 focus:shadow-lg dark:focus:bg-gray-900 dark:focus:text-white">Skip to main content</a>;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== isBrochurePage) {
    t3 = !isBrochurePage && <Preloader />;
    $[1] = isBrochurePage;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <RouteScrollReset />;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== isBrochurePage) {
    t5 = !isBrochurePage && <Header />;
    $[4] = isBrochurePage;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== children || $[7] !== isBrochurePage) {
    t6 = isBrochurePage ? children : <PageTransition contentRef={contentRef}><div ref={contentRef} id="main-content" tabIndex={-1} className="relative z-10 bg-white outline-none dark:bg-gray-950"><HomeTextAnimations>{children}</HomeTextAnimations></div></PageTransition>;
    $[6] = children;
    $[7] = isBrochurePage;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== isBrochurePage) {
    t7 = !isBrochurePage && <Footer />;
    $[9] = isBrochurePage;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== isBrochurePage) {
    t8 = !isBrochurePage && <ScrollToTop />;
    $[11] = isBrochurePage;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <ToastHost />;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t1 || $[15] !== t3 || $[16] !== t5 || $[17] !== t6 || $[18] !== t7 || $[19] !== t8) {
    t10 = <ThemeProvider><MotionConfig reducedMotion={t1}>{t2}{t3}{t4}{t5}{t6}{t7}{t8}{t9}</MotionConfig></ThemeProvider>;
    $[14] = t1;
    $[15] = t3;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  return t10;
}